import React from 'react';
import { Card, CardContent, Typography, IconButton, Box, Chip, Tooltip } from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Flag as FlagIcon } from '@mui/icons-material';
import { Task, TaskStatus } from '../types/task';
import { format } from 'date-fns';

interface TaskCardProps {
    task: Task;
    onEdit: (task: Task) => void;
    onDelete: (taskId: number) => void;
}

const getStatusColor = (status: TaskStatus) => {
    switch (status) {
        case TaskStatus.TODO:
            return 'error';
        case TaskStatus.IN_PROGRESS:
            return 'warning';
        case TaskStatus.DONE:
            return 'success';
        default:
            return 'default';
    }
};

const getPriorityColor = (priority: number) => {
    // Extended color spectrum from red (highest priority) to green (lowest priority)
    // Goes through orange, yellow, yellow-green in between
    const colors = [
        '#d32f2f', // 1 - Highest priority (deep red)
        '#e53935', 
        '#f44336',
        '#ff5722', // Orange-red
        '#ff7043',
        '#ff9800', // Orange
        '#ffa726', 
        '#ffb74d',
        '#ffc107', // Amber/yellow
        '#ffca28',
        '#ffd54f',
        '#ffe082',
        '#cddc39', // Lime
        '#c0ca33',
        '#aed581',
        '#9ccc65',
        '#8bc34a', // Light green
        '#7cb342',
        '#66bb6a',
        '#4caf50'  // 20 - Lowest priority (green)
    ];
    
    // Normalize priority to our color scale (1-20)
    // This maps priority 1-10 across our 20 colors
    const index = Math.max(1, Math.min(10, priority));
    const normalizedIndex = Math.floor((index - 1) * 2); // Maps 1-10 to 0-18 (by increments of 2)
    
    return colors[normalizedIndex];
};

// Determine if text should be dark or light based on background color
const getTextColor = (backgroundColor: string) => {
    // Simple luminance calculation - will return 'white' for dark backgrounds and 'black' for light backgrounds
    // Convert hex to RGB
    const r = parseInt(backgroundColor.slice(1, 3), 16);
    const g = parseInt(backgroundColor.slice(3, 5), 16);
    const b = parseInt(backgroundColor.slice(5, 7), 16);
    
    // Calculate perceived brightness (ITU-R BT.709)
    const brightness = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
    
    return brightness > 0.5 ? '#000000' : '#ffffff';
};

const TaskCard: React.FC<TaskCardProps> = ({ task, onEdit, onDelete }) => {
    const backgroundColor = getPriorityColor(task.priority);
    const textColor = getTextColor(backgroundColor);
    
    return (
        <Card 
            sx={{ 
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: backgroundColor,
                transition: 'transform 0.2s ease-in-out',
                '&:hover': {
                    transform: 'scale(1.02)',
                    boxShadow: 3
                }
            }}
        >
            <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', color: textColor, p: 2 }}>
                <Typography variant="h6" component="div" noWrap sx={{ width: '100%', color: textColor, mb: 1 }}>
                    {task.title}
                </Typography>
                
                <Typography 
                    variant="body2" 
                    sx={{ 
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        flexGrow: 1,
                        color: textColor,
                        opacity: 0.85,
                        mb: 2
                    }}
                >
                    {task.description}
                </Typography>
                
                <Box sx={{ 
                    mt: 'auto', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    flexWrap: 'wrap', 
                    gap: 0.5 
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Tooltip title={`Priority: ${task.priority}`}>
                            <Chip
                                icon={<FlagIcon fontSize="small" />}
                                label={`P${task.priority}`}
                                size="small"
                                sx={{ 
                                    backgroundColor: 'rgba(255, 255, 255, 0.25)',
                                    backdropFilter: 'blur(4px)',
                                    color: textColor,
                                    fontWeight: 'bold'
                                }}
                            />
                        </Tooltip>
                        <Typography variant="caption" sx={{ color: textColor, opacity: 0.85 }}>
                            Due: {new Date(task.dueDate).toLocaleDateString()}
                        </Typography>
                    </Box>
                    
                    <Box>
                        <IconButton 
                            size="small" 
                            onClick={() => onEdit(task)} 
                            title="Edit task" 
                            sx={{ 
                                color: textColor,
                                p: 0.5,
                                opacity: 0.7,
                                '&:hover': { opacity: 1 }
                            }}
                        >
                            <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton 
                            size="small" 
                            onClick={() => onDelete(task.id)} 
                            title="Delete task" 
                            sx={{ 
                                color: textColor,
                                p: 0.5,
                                opacity: 0.7,
                                '&:hover': { opacity: 1 }
                            }}
                        >
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
};

export default TaskCard; 